/**
 * InlineEditModal - Modal for editing single WordPress content items
 * Supports posts, pages, media, and users with content-type-specific fields
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth';
import { config } from '@/config';
import { useTaxonomies, type TaxonomyTerm } from '@/hooks/useTaxonomies';
import { useAuthors, type AuthorOption } from '@/hooks/useAuthors';

// Content types supported by this modal
type ContentType = 'posts' | 'pages' | 'media' | 'users';

// Item shape passed to the modal
interface ContentItem {
  id: string;           // Our UUID
  wp_id: number;        // WordPress ID
  title: string;
  metadata: Record<string, unknown>;
}

interface InlineEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  siteId: string;
  contentType: ContentType;
  item: ContentItem;
  onSuccess?: () => void;
  onEditContent?: () => void; // Callback to open content editor modal
}

// Field definition for the form
interface FieldDefinition {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'date' | 'multiselect' | 'taxonomy-multiselect' | 'checkbox' | 'password' | 'visibility-radio';
  options?: { value: string; label: string }[];
  dangerous?: boolean;
  helpText?: string;
  placeholder?: string;
  contentTypeFilter?: ContentType[]; // Only show for specific content types
  section?: 'status' | 'visibility' | 'content' | 'metadata'; // Group fields into sections
  essential?: boolean; // Essential fields are always shown and can't be hidden
  defaultVisible?: boolean; // Non-essential fields shown by default
}

// Field group configuration for settings dropdown
interface FieldGroup {
  label: string;
  fields: string[]; // field keys
}

// Field groups by content type for organized settings dropdown
const FIELD_GROUPS: Record<ContentType, FieldGroup[]> = {
  posts: [
    {
      label: 'CONTENT',
      fields: ['title', 'slug', 'excerpt', 'date', 'author', 'featured_media'],
    },
    {
      label: 'PUBLISH SETTINGS',
      fields: ['status', 'visibility', 'password', 'sticky'],
    },
    {
      label: 'TAXONOMY',
      fields: ['categories', 'tags'],
    },
    {
      label: 'DISCUSSION',
      fields: ['comment_status'],
    },
  ],
  pages: [
    {
      label: 'CONTENT',
      fields: ['title', 'slug', 'excerpt', 'date', 'featured_media'],
    },
    {
      label: 'PUBLISH SETTINGS',
      fields: ['status', 'visibility', 'password'],
    },
    {
      label: 'DISCUSSION',
      fields: ['comment_status'],
    },
  ],
  media: [
    {
      label: 'CONTENT',
      fields: ['title', 'alt_text', 'caption', 'description'],
    },
  ],
  users: [
    {
      label: 'PROFILE',
      fields: ['name', 'email', 'url', 'description'],
    },
  ],
};

// Status options (pre-publish workflow states)
const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'pending', label: 'Pending Review' },
];

// Field configurations by content type
const FIELD_CONFIGS: Record<ContentType, FieldDefinition[]> = {
  posts: [
    // Content section
    { key: 'title', label: 'Title', type: 'text', placeholder: 'Post title', section: 'content', essential: true },
    { key: 'slug', label: 'Slug', type: 'text', placeholder: 'post-url-slug', helpText: 'The URL-friendly name of the post', section: 'content', essential: true },
    { key: 'excerpt', label: 'Excerpt', type: 'textarea', placeholder: 'Brief summary of the post...', section: 'content', defaultVisible: true },
    { key: 'date', label: 'Publish Date', type: 'date', section: 'content', essential: true },
    { key: 'author', label: 'Author', type: 'select', helpText: 'Select the post author', section: 'content', defaultVisible: true },

    // Status section
    { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS, section: 'status', essential: true },

    // Visibility section
    { key: 'visibility', label: 'Visibility', type: 'visibility-radio', section: 'visibility', dangerous: true, essential: true },
    { key: 'password', label: 'Password', type: 'password', placeholder: 'Enter password', helpText: 'Readers must enter this password', section: 'visibility' },
    { key: 'sticky', label: 'Sticky', type: 'checkbox', helpText: 'Stick this post to the front page', section: 'visibility' },

    // Metadata section
    { key: 'categories', label: 'Categories', type: 'taxonomy-multiselect', helpText: 'Select categories', section: 'metadata', defaultVisible: true },
    { key: 'tags', label: 'Tags', type: 'taxonomy-multiselect', helpText: 'Select tags', section: 'metadata', defaultVisible: true },
    { key: 'comment_status', label: 'Comments', type: 'select', options: [
      { value: 'open', label: 'Open' },
      { value: 'closed', label: 'Closed' },
    ], section: 'metadata' },
    { key: 'featured_media', label: 'Featured Media ID', type: 'text', helpText: 'WordPress media attachment ID', section: 'metadata' },
  ],
  pages: [
    // Content section
    { key: 'title', label: 'Title', type: 'text', placeholder: 'Page title', section: 'content', essential: true },
    { key: 'slug', label: 'Slug', type: 'text', placeholder: 'page-url-slug', helpText: 'The URL-friendly name of the page', section: 'content', essential: true },
    { key: 'excerpt', label: 'Excerpt', type: 'textarea', placeholder: 'Brief summary of the page...', section: 'content', defaultVisible: true },
    { key: 'date', label: 'Publish Date', type: 'date', section: 'content', essential: true },

    // Status section
    { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS, section: 'status', essential: true },

    // Visibility section
    { key: 'visibility', label: 'Visibility', type: 'visibility-radio', section: 'visibility', dangerous: true, essential: true },
    { key: 'password', label: 'Password', type: 'password', placeholder: 'Enter password', helpText: 'Readers must enter this password', section: 'visibility' },

    // Metadata section
    { key: 'comment_status', label: 'Comments', type: 'select', options: [
      { value: 'open', label: 'Open' },
      { value: 'closed', label: 'Closed' },
    ], section: 'metadata' },
    { key: 'featured_media', label: 'Featured Media ID', type: 'text', helpText: 'WordPress media attachment ID', section: 'metadata' },
  ],
  media: [
    { key: 'title', label: 'Title', type: 'text', placeholder: 'Media title', essential: true },
    { key: 'alt_text', label: 'Alt Text', type: 'text', placeholder: 'Describe the image for accessibility', helpText: 'Alternative text for screen readers', defaultVisible: true },
    { key: 'caption', label: 'Caption', type: 'textarea', placeholder: 'Media caption...' },
    { key: 'description', label: 'Description', type: 'textarea', placeholder: 'Detailed description...' },
  ],
  users: [
    { key: 'name', label: 'Display Name', type: 'text', placeholder: 'Display name' },
    { key: 'first_name', label: 'First Name', type: 'text', placeholder: 'First name' },
    { key: 'last_name', label: 'Last Name', type: 'text', placeholder: 'Last name' },
    { key: 'nickname', label: 'Nickname', type: 'text', placeholder: 'Nickname' },
    { key: 'description', label: 'Biographical Info', type: 'textarea', placeholder: 'User biography...' },
    { key: 'url', label: 'Website', type: 'text', placeholder: 'https://example.com' },
  ],
};

// API error response shape
interface ApiErrorResponse {
  error?: string;
  message?: string;
  conflict?: boolean;
  serverData?: Record<string, unknown>;
}

// Edit response shape
interface EditResponse {
  success: boolean;
  message?: string;
}

// Helper to compute visibility from status and password
function getVisibilityValue(status: unknown, password: unknown): string {
  const statusStr = String(status || 'draft');
  const passwordStr = String(password || '');

  if (statusStr === 'private') return 'private';
  if (passwordStr && statusStr === 'publish') return 'password';
  return 'public';
}

// Helper to update status/password based on visibility change
function applyVisibilityChange(
  visibility: string,
  currentData: Record<string, unknown>
): Record<string, unknown> {
  const updates = { ...currentData };

  switch (visibility) {
    case 'public':
      updates.status = 'publish';
      updates.password = '';
      break;
    case 'private':
      updates.status = 'private';
      updates.password = '';
      updates.sticky = false; // Private posts can't be sticky
      break;
    case 'password':
      updates.status = 'publish';
      // Keep existing password or set empty (user will fill it)
      if (!updates.password) updates.password = '';
      updates.sticky = false; // Password-protected posts can't be sticky
      break;
  }

  return updates;
}

// VisibilityRadio component for WordPress-style visibility control
function VisibilityRadio({
  value,
  onChange,
  showSticky,
  stickyValue,
  onStickyChange,
  passwordValue,
  onPasswordChange,
}: {
  value: string;
  onChange: (visibility: string) => void;
  showSticky?: boolean;
  stickyValue?: boolean;
  onStickyChange?: (checked: boolean) => void;
  passwordValue?: string;
  onPasswordChange?: (password: string) => void;
}) {
  return (
    <div className="space-y-3">
      {/* Public */}
      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="radio"
          name="visibility"
          value="public"
          checked={value === 'public'}
          onChange={(e) => onChange(e.target.value)}
          className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
        />
        <div className="flex-1">
          <div className="font-medium text-gray-900">Public</div>
          <div className="text-xs text-gray-500">Visible to everyone</div>
          {value === 'public' && showSticky && (
            <label className="flex items-center gap-2 mt-2 ml-6">
              <input
                type="checkbox"
                checked={stickyValue}
                onChange={(e) => onStickyChange?.(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Stick this post to the front page</span>
            </label>
          )}
        </div>
      </label>

      {/* Private */}
      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="radio"
          name="visibility"
          value="private"
          checked={value === 'private'}
          onChange={(e) => onChange(e.target.value)}
          className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
        />
        <div className="flex-1">
          <div className="font-medium text-gray-900">Private</div>
          <div className="text-xs text-gray-500">Only visible to site admins and editors</div>
        </div>
      </label>

      {/* Password Protected */}
      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="radio"
          name="visibility"
          value="password"
          checked={value === 'password'}
          onChange={(e) => onChange(e.target.value)}
          className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
        />
        <div className="flex-1">
          <div className="font-medium text-gray-900">Password Protected</div>
          <div className="text-xs text-gray-500">Readers must enter password to view</div>
          {value === 'password' && (
            <input
              type="text"
              value={passwordValue || ''}
              onChange={(e) => onPasswordChange?.(e.target.value)}
              placeholder="Enter password"
              className="mt-2 ml-6 w-full max-w-xs px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          )}
        </div>
      </label>
    </div>
  );
}

// TaxonomyMultiSelect component for categories/tags
function TaxonomyMultiSelect({
  terms,
  selectedIds,
  onChange,
  isLoading,
  placeholder,
}: {
  terms: TaxonomyTerm[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  isLoading: boolean;
  placeholder: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredTerms = useMemo(() => {
    if (!search) return terms;
    const lower = search.toLowerCase();
    return terms.filter(t => t.label.toLowerCase().includes(lower));
  }, [terms, search]);

  const selectedTerms = useMemo(
    () => terms.filter(t => selectedIds.includes(Number(t.value))),
    [terms, selectedIds]
  );

  const toggleTerm = (termId: number) => {
    if (selectedIds.includes(termId)) {
      onChange(selectedIds.filter(id => id !== termId));
    } else {
      onChange([...selectedIds, termId]);
    }
  };

  if (isLoading) {
    return (
      <div className="px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 text-sm">
        Loading...
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between gap-2 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <div className="flex-1 flex flex-wrap gap-1 min-h-[1.25rem]">
          {selectedTerms.length === 0 ? (
            <span className="text-gray-500 text-sm">{placeholder}</span>
          ) : (
            selectedTerms.map(term => (
              <span
                key={term.value}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs"
              >
                {term.label}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleTerm(Number(term.value));
                  }}
                  className="hover:text-blue-600"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            ))
          )}
        </div>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              autoFocus
            />
          </div>
          <div className="overflow-y-auto max-h-48">
            {filteredTerms.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">No items found</div>
            ) : (
              filteredTerms.map(term => (
                <label
                  key={term.value}
                  className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(Number(term.value))}
                    onChange={() => toggleTerm(Number(term.value))}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 flex-1">{term.label}</span>
                  <span className="text-xs text-gray-400">({term.count})</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function InlineEditModal({
  isOpen,
  onClose,
  siteId,
  contentType,
  item,
  onSuccess,
  onEditContent,
}: InlineEditModalProps) {
  const queryClient = useQueryClient();

  // Form state - stores field values
  const [formData, setFormData] = useState<Record<string, unknown>>({});

  // UI state
  const [error, setError] = useState<string | null>(null);
  const [conflictData, setConflictData] = useState<Record<string, unknown> | null>(null);
  const [dangerousFieldsConfirmed, setDangerousFieldsConfirmed] = useState<Set<string>>(new Set());
  const [showFieldSettings, setShowFieldSettings] = useState(false);
  const settingsDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showFieldSettings && settingsDropdownRef.current && !settingsDropdownRef.current.contains(event.target as Node)) {
        setShowFieldSettings(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFieldSettings]);

  // Get field definitions for the current content type FIRST (needed for initial state)
  const fields = useMemo(() => FIELD_CONFIGS[contentType] || [], [contentType]);

  // Field visibility preferences - load from localStorage
  const getFieldPreferences = (): Set<string> => {
    try {
      const stored = localStorage.getItem(`modal-fields-${contentType}`);
      if (stored) {
        return new Set(JSON.parse(stored));
      }
    } catch (e) {
      console.error('Failed to load field preferences:', e);
    }

    // Default: show essential fields + fields marked as defaultVisible
    const defaultFields = fields
      .filter(f => f.essential || f.defaultVisible)
      .map(f => f.key);
    return new Set(defaultFields);
  };

  const [visibleFields, setVisibleFields] = useState<Set<string>>(getFieldPreferences());

  // Save field preferences to localStorage when changed
  const toggleFieldVisibility = (fieldKey: string) => {
    setVisibleFields(prev => {
      const next = new Set(prev);
      if (next.has(fieldKey)) {
        next.delete(fieldKey);
      } else {
        next.add(fieldKey);
      }

      // Save to localStorage
      try {
        localStorage.setItem(`modal-fields-${contentType}`, JSON.stringify(Array.from(next)));
      } catch (e) {
        console.error('Failed to save field preferences:', e);
      }

      return next;
    });
  };

  // Fetch taxonomies for categories/tags (only for posts)
  const { categories, tags, isLoading: taxonomiesLoading } = useTaxonomies(
    contentType === 'posts' ? siteId : undefined
  );

  // Fetch authors for posts/pages
  const { data: authors = [], isLoading: authorsLoading } = useAuthors(
    contentType === 'posts' || contentType === 'pages' ? siteId : undefined
  );

  // Get dangerous fields that have been modified
  const modifiedDangerousFields = useMemo(() => {
    return fields.filter(f => {
      if (!f.dangerous) return false;
      const originalValue = getFieldValue(item, f.key);
      const currentValue = formData[f.key];
      return currentValue !== undefined && currentValue !== originalValue;
    });
  }, [fields, formData, item]);

  // Check if all dangerous field changes are confirmed
  const allDangerousConfirmed = useMemo(() => {
    return modifiedDangerousFields.every(f => dangerousFieldsConfirmed.has(f.key));
  }, [modifiedDangerousFields, dangerousFieldsConfirmed]);

  // Reset form when modal opens or item changes
  useEffect(() => {
    if (isOpen) {
      // Initialize form with current item values
      const initialData: Record<string, unknown> = {};
      fields.forEach(field => {
        initialData[field.key] = getFieldValue(item, field.key);
      });
      setFormData(initialData);
      setError(null);
      setConflictData(null);
      setDangerousFieldsConfirmed(new Set());
    }
  }, [isOpen, item, fields]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: { updates: Record<string, unknown>; force?: boolean }) => {
      const token = useAuthStore.getState().token;
      const apiUrl = config.apiUrl;

      const response = await fetch(
        `${apiUrl}/api/v1/sites/${siteId}/content/${contentType}/${item.wp_id}/edit`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            updates: data.updates,
            force: data.force,
          }),
        }
      );

      if (response.status === 409) {
        const errorData = await response.json() as ApiErrorResponse;
        // Conflict detected
        return {
          conflict: true,
          serverData: errorData.serverData,
          message: errorData.message || 'This item was modified on WordPress since your last sync.',
        };
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Save failed' })) as ApiErrorResponse;
        throw new Error(errorData.message || 'Save failed');
      }

      return response.json() as Promise<EditResponse>;
    },
    onSuccess: (result) => {
      if (result && 'conflict' in result && result.conflict) {
        // Show conflict warning
        setConflictData(result.serverData || null);
        setError(result.message || 'Conflict detected');
        return;
      }

      // Success - invalidate queries and close
      queryClient.invalidateQueries({ queryKey: ['site-content', siteId] });
      onSuccess?.();
      onClose();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    },
  });

  // Handle field change
  const handleFieldChange = (key: string, value: unknown) => {
    setFormData(prev => {
      const updated = { ...prev, [key]: value };

      // Special handling: visibility changes affect status/password/sticky
      if (key === 'visibility') {
        return applyVisibilityChange(String(value), updated);
      }

      return updated;
    });

    // Clear conflict when user makes changes
    if (conflictData) {
      setConflictData(null);
      setError(null);
    }
  };

  // Handle dangerous field confirmation toggle
  const handleDangerousConfirmation = (key: string, confirmed: boolean) => {
    setDangerousFieldsConfirmed(prev => {
      const next = new Set(prev);
      if (confirmed) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  // Handle save
  const handleSave = (force = false) => {
    setError(null);

    // Build updates payload (only include changed fields)
    const updates: Record<string, unknown> = {};
    fields.forEach(field => {
      const originalValue = getFieldValue(item, field.key);
      const currentValue = formData[field.key];
      if (currentValue !== originalValue && currentValue !== undefined) {
        updates[field.key] = currentValue;
      }
    });

    if (Object.keys(updates).length === 0) {
      onClose();
      return;
    }

    // Check dangerous field confirmations
    if (!force && !allDangerousConfirmed && modifiedDangerousFields.length > 0) {
      setError('Please confirm the dangerous field changes before saving.');
      return;
    }

    saveMutation.mutate({ updates, force });
  };

  // Handle "Send Anyway" for conflicts
  const handleForceSubmit = () => {
    handleSave(true);
  };

  if (!isOpen) return null;

  const hasChanges = fields.some(field => {
    const originalValue = getFieldValue(item, field.key);
    const currentValue = formData[field.key];
    return currentValue !== undefined && currentValue !== originalValue;
  });

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Edit {contentType.charAt(0).toUpperCase() + contentType.slice(1, -1)}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {item.title} (WP ID: {item.wp_id})
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Field Settings Button */}
            <div className="relative" ref={settingsDropdownRef}>
              <button
                onClick={() => setShowFieldSettings(!showFieldSettings)}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded hover:bg-gray-100"
                title="Show/Hide Fields"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>

              {/* Field Settings Dropdown */}
              {showFieldSettings && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-10 max-h-96 overflow-y-auto">
                  <div className="px-4 py-3 border-b border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-700">Show/Hide Fields</h3>
                    <p className="text-xs text-gray-500 mt-1">Customize which fields appear in this form</p>
                  </div>

                  {FIELD_GROUPS[contentType]?.map((group) => (
                    <div key={group.label} className="px-4 py-3 border-b border-gray-100 last:border-b-0">
                      <div className="text-xs font-semibold text-gray-500 mb-2">{group.label}</div>
                      <div className="space-y-2">
                        {group.fields.map((fieldKey) => {
                          const fieldDef = fields.find(f => f.key === fieldKey);
                          if (!fieldDef) return null;

                          const isEssential = fieldDef.essential;
                          const isChecked = visibleFields.has(fieldKey);

                          return (
                            <label
                              key={fieldKey}
                              className={`flex items-center gap-2 text-sm ${
                                isEssential ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'
                              } px-2 py-1 rounded`}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                disabled={isEssential}
                                onChange={() => !isEssential && toggleFieldVisibility(fieldKey)}
                                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50"
                              />
                              <span className="flex-1 text-gray-700">{fieldDef.label}</span>
                              {isEssential && (
                                <span className="text-xs text-gray-400">(required)</span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body - scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Error message */}
          {error && !conflictData && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Conflict warning */}
          {conflictData && (
            <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-yellow-800">
                    Content Conflict Detected
                  </h4>
                  <p className="text-sm text-yellow-700 mt-1">
                    This item was modified on WordPress since your last sync. You can force your changes or cancel to review the current version.
                  </p>
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={handleForceSubmit}
                      disabled={saveMutation.isPending}
                      className="px-3 py-1.5 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50"
                    >
                      {saveMutation.isPending ? 'Sending...' : 'Send Anyway'}
                    </button>
                    <button
                      onClick={() => {
                        setConflictData(null);
                        setError(null);
                      }}
                      className="px-3 py-1.5 text-sm text-yellow-700 hover:text-yellow-800"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Edit Content button (for posts/pages) */}
          {(contentType === 'posts' || contentType === 'pages') && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-blue-900">Full Content Editor</h4>
                  <p className="text-sm text-blue-700 mt-0.5">
                    Edit the main content with the rich text editor
                  </p>
                </div>
                <button
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  onClick={() => {
                    if (onEditContent) {
                      onClose(); // Close this modal first
                      onEditContent();
                    }
                  }}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit Content
                </button>
              </div>
            </div>
          )}

          {/* Form fields grouped by section */}
          {fields
            // Filter to only show visible fields
            .filter(field => visibleFields.has(field.key))
            // Filter out password/sticky if visibility field exists (they're embedded in VisibilityRadio)
            .filter(field => {
              const hasVisibility = fields.some(f => f.key === 'visibility');
              if (hasVisibility && (field.key === 'password' || field.key === 'sticky')) {
                return false; // Skip - these are handled by VisibilityRadio
              }
              return true;
            })
            .map((field, index, visibleFields) => {
              // Show section header if this is the first field in a new section
              const prevField = index > 0 ? visibleFields[index - 1] : null;
              const showSectionHeader = field.section && field.section !== prevField?.section;

              return (
                <div key={field.key}>
                  {showSectionHeader && (
                    <div className="mt-6 mb-3 pb-2 border-b border-gray-200">
                      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                        {field.section}
                      </h3>
                    </div>
                  )}
                  <FieldRenderer
                    field={field}
                    value={formData[field.key]}
                    originalValue={getFieldValue(item, field.key)}
                    onChange={(value) => handleFieldChange(field.key, value)}
                    isConfirmed={dangerousFieldsConfirmed.has(field.key)}
                    onConfirmationChange={(confirmed) => handleDangerousConfirmation(field.key, confirmed)}
                    categories={categories}
                    tags={tags}
                    taxonomiesLoading={taxonomiesLoading}
                    authors={authors}
                    authorsLoading={authorsLoading}
                    formData={formData}
                    contentType={contentType}
                    onOtherFieldChange={handleFieldChange}
                  />
                </div>
              );
            })}
          {/* Changes Summary */}
          {Object.keys(formData).some(key => {
            const originalValue = getFieldValue(item, key);
            return formData[key] !== originalValue && formData[key] !== undefined;
          }) && (
            <div className="mt-6 px-6 py-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Changes Summary
              </h4>
              <div className="space-y-2">
                {Object.entries(formData)
                  .filter(([key, value]) => {
                    const originalValue = getFieldValue(item, key);
                    return value !== originalValue && value !== undefined;
                  })
                  .map(([key, value]) => {
                    const field = fields.find(f => f.key === key);
                    const originalValue = getFieldValue(item, key);

                    return (
                      <div key={key} className="flex items-start gap-3 text-sm">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-gray-700">{field?.label || key}</div>
                          <div className="flex items-center gap-2 mt-1">
                            <div className="flex-1 min-w-0">
                              <span className="text-gray-500">Was: </span>
                              <span className="text-gray-900 font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                                {String(originalValue || '(empty)').slice(0, 50)}
                                {String(originalValue || '').length > 50 && '...'}
                              </span>
                            </div>
                            <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                            <div className="flex-1 min-w-0">
                              <span className="text-gray-500">Now: </span>
                              <span className="text-blue-900 font-mono text-xs bg-blue-100 px-1.5 py-0.5 rounded">
                                {String(value || '(empty)').slice(0, 50)}
                                {String(value || '').length > 50 && '...'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
          >
            Cancel
          </button>
          <button
            onClick={() => handleSave(false)}
            disabled={saveMutation.isPending || !hasChanges || (modifiedDangerousFields.length > 0 && !allDangerousConfirmed)}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Field renderer component
interface FieldRendererProps {
  field: FieldDefinition;
  value: unknown;
  originalValue: unknown;
  onChange: (value: unknown) => void;
  isConfirmed: boolean;
  onConfirmationChange: (confirmed: boolean) => void;
  categories?: TaxonomyTerm[];
  tags?: TaxonomyTerm[];
  taxonomiesLoading?: boolean;
  authors?: AuthorOption[];
  authorsLoading?: boolean;
  formData?: Record<string, unknown>; // For visibility-radio to access password/sticky
  contentType?: ContentType; // To know if we should show sticky option
  onOtherFieldChange?: (key: string, value: unknown) => void; // For visibility-radio to update sticky/password
}

function FieldRenderer({
  field,
  value,
  originalValue,
  onChange,
  isConfirmed,
  onConfirmationChange,
  categories = [],
  tags = [],
  taxonomiesLoading = false,
  authors = [],
  authorsLoading = false,
  formData = {},
  contentType = 'posts',
  onOtherFieldChange,
}: FieldRendererProps) {
  const isModified = value !== originalValue && value !== undefined;
  const isDangerous = field.dangerous && isModified;

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <label className="block text-sm font-medium text-gray-700">
          {field.label}
        </label>
        {field.dangerous && (
          <span className="inline-flex items-center gap-1 text-xs text-amber-600">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Sensitive
          </span>
        )}
        {isModified && (
          <span className="text-xs text-blue-600">Modified</span>
        )}
      </div>

      {/* Render different input types */}
      {field.type === 'text' && (
        <input
          type="text"
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            isDangerous ? 'border-amber-300 bg-amber-50' : 'border-gray-300'
          }`}
        />
      )}

      {field.type === 'textarea' && (
        <textarea
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          rows={3}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            isDangerous ? 'border-amber-300 bg-amber-50' : 'border-gray-300'
          }`}
        />
      )}

      {field.type === 'select' && (field.options || field.key === 'author') && (
        <select
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          disabled={field.key === 'author' && authorsLoading}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            isDangerous ? 'border-amber-300 bg-amber-50' : 'border-gray-300'
          }`}
        >
          <option value="">{field.key === 'author' && authorsLoading ? 'Loading...' : 'Select...'}</option>
          {field.key === 'author'
            ? authors.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))
            : field.options?.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))
          }
        </select>
      )}

      {field.type === 'date' && (
        <input
          type="datetime-local"
          value={formatDateForInput(value)}
          onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : '')}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            isDangerous ? 'border-amber-300 bg-amber-50' : 'border-gray-300'
          }`}
        />
      )}

      {field.type === 'multiselect' && (
        <input
          type="text"
          value={Array.isArray(value) ? value.join(', ') : String(value ?? '')}
          onChange={(e) => {
            const values = e.target.value.split(',').map(v => v.trim()).filter(Boolean);
            onChange(values.length > 0 ? values : []);
          }}
          placeholder={field.placeholder || 'Enter values separated by commas'}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            isDangerous ? 'border-amber-300 bg-amber-50' : 'border-gray-300'
          }`}
        />
      )}

      {field.type === 'taxonomy-multiselect' && field.key === 'categories' && (
        <TaxonomyMultiSelect
          terms={categories}
          selectedIds={Array.isArray(value) ? value.map(v => Number(v)) : []}
          onChange={(ids) => onChange(ids)}
          isLoading={taxonomiesLoading}
          placeholder="Select categories..."
        />
      )}

      {field.type === 'taxonomy-multiselect' && field.key === 'tags' && (
        <TaxonomyMultiSelect
          terms={tags}
          selectedIds={Array.isArray(value) ? value.map(v => Number(v)) : []}
          onChange={(ids) => onChange(ids)}
          isLoading={taxonomiesLoading}
          placeholder="Select tags..."
        />
      )}

      {field.type === 'checkbox' && (
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">{field.helpText || 'Enable'}</span>
        </label>
      )}

      {field.type === 'password' && (
        <input
          type="text"
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            isDangerous ? 'border-amber-300 bg-amber-50' : 'border-gray-300'
          }`}
        />
      )}

      {field.type === 'visibility-radio' && (
        <VisibilityRadio
          value={String(value || 'public')}
          onChange={(newVisibility) => {
            // Changing visibility triggers applyVisibilityChange in handleFieldChange
            onChange(newVisibility);
          }}
          showSticky={contentType === 'posts'}
          stickyValue={Boolean(formData.sticky)}
          onStickyChange={onOtherFieldChange ? (checked) => onOtherFieldChange('sticky', checked) : undefined}
          passwordValue={String(formData.password || '')}
          onPasswordChange={onOtherFieldChange ? (pwd) => onOtherFieldChange('password', pwd) : undefined}
        />
      )}

      {/* Help text (but not for checkbox since it's used as label) */}
      {field.helpText && field.type !== 'checkbox' && (
        <p className="mt-1 text-xs text-gray-500">{field.helpText}</p>
      )}

      {/* Dangerous field confirmation */}
      {isDangerous && (
        <div className="mt-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <input
            type="checkbox"
            id={`confirm-${field.key}`}
            checked={isConfirmed}
            onChange={(e) => onConfirmationChange(e.target.checked)}
            className="mt-0.5 w-4 h-4 text-amber-600 rounded border-amber-300 focus:ring-amber-500"
          />
          <label htmlFor={`confirm-${field.key}`} className="text-xs text-amber-800">
            I understand that changing {field.label.toLowerCase()} from "{String(originalValue)}" to "{String(value)}" may affect how this content appears on the live site.
          </label>
        </div>
      )}
    </div>
  );
}

// Helper to get field value from item
function getFieldValue(item: ContentItem, key: string): unknown {
  // Special handling first (before generic metadata check)
  if (key === 'title') return item.title;

  // Slug might be stored as 'slug' or 'post_name' in metadata
  if (key === 'slug' && item.metadata) {
    return item.metadata.slug || item.metadata.post_name || '';
  }

  // Visibility is computed from status + password
  if (key === 'visibility' && item.metadata) {
    return getVisibilityValue(item.metadata.status, item.metadata.password);
  }

  // Then check metadata generically
  if (item.metadata && key in item.metadata) {
    const value = item.metadata[key];

    // Handle nested rendered objects (WordPress REST API format)
    if (typeof value === 'object' && value !== null && 'rendered' in value) {
      return (value as { rendered: string }).rendered;
    }

    return value;
  }

  return undefined;
}

// Helper to format date for datetime-local input
function formatDateForInput(value: unknown): string {
  if (!value) return '';
  try {
    const date = new Date(String(value));
    if (isNaN(date.getTime())) return '';
    // Format as YYYY-MM-DDTHH:mm
    return date.toISOString().slice(0, 16);
  } catch {
    return '';
  }
}

export default InlineEditModal;
